import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, CheckCircle2, Clock, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const modules = [
  { week: "Module 1", title: "Foundation & Entrepreneurial Mindset", duration: "2 hours", skoolUrl: "https://www.skool.com/launch2revenue" },
  { week: "Module 2", title: "Market Research & Validation", duration: "2 hours", skoolUrl: "https://www.skool.com/launch2revenue" },
  { week: "Module 3", title: "Niche Selection & Positioning", duration: "2 hours", skoolUrl: "https://www.skool.com/launch2revenue" },
  { week: "Module 4", title: "Business Model Design", duration: "2 hours", skoolUrl: "https://www.skool.com/launch2revenue" },
  { week: "Module 5", title: "Brand Identity & Messaging", duration: "2 hours", skoolUrl: "https://www.skool.com/launch2revenue" },
  { week: "Module 6", title: "Website & Landing Page Setup", duration: "3 hours", skoolUrl: "https://www.skool.com/launch2revenue" },
  { week: "Module 7", title: "Content Marketing Fundamentals", duration: "2 hours", skoolUrl: "https://www.skool.com/launch2revenue" },
  { week: "Module 8", title: "Social Media Strategy", duration: "2 hours", skoolUrl: "https://www.skool.com/launch2revenue" },
  { week: "Module 9", title: "Email Marketing & List Building", duration: "2 hours", skoolUrl: "https://www.skool.com/launch2revenue" },
  { week: "Module 10", title: "SEO & Organic Traffic", duration: "2 hours", skoolUrl: "https://www.skool.com/launch2revenue" },
  { week: "Module 11", title: "Paid Advertising Basics", duration: "2 hours", skoolUrl: "https://www.skool.com/launch2revenue" },
  { week: "Module 12", title: "Sales Funnel Creation", duration: "3 hours", skoolUrl: "https://www.skool.com/launch2revenue" },
  { week: "Module 13", title: "Conversion Optimization", duration: "2 hours", skoolUrl: "https://www.skool.com/launch2revenue" },
  { week: "Module 14", title: "Sales Psychology & Copywriting", duration: "2 hours", skoolUrl: "https://www.skool.com/launch2revenue" },
  { week: "Module 15", title: "Pricing Strategy & Offers", duration: "2 hours", skoolUrl: "https://www.skool.com/launch2revenue" },
  { week: "Module 16", title: "Customer Acquisition Strategy", duration: "2 hours", skoolUrl: "https://www.skool.com/launch2revenue" },
  { week: "Module 17", title: "Pre-Launch Planning", duration: "2 hours", skoolUrl: "https://www.skool.com/launch2revenue" },
  { week: "Module 18", title: "Launch Execution & Tactics", duration: "3 hours", skoolUrl: "https://www.skool.com/launch2revenue" },
  { week: "Module 19", title: "Post-Launch Optimization", duration: "2 hours", skoolUrl: "https://www.skool.com/launch2revenue" },
  { week: "Module 20", title: "Customer Retention & Loyalty", duration: "2 hours", skoolUrl: "https://www.skool.com/launch2revenue" },
  { week: "Module 21", title: "Scaling Your Operations", duration: "2 hours", skoolUrl: "https://www.skool.com/launch2revenue" },
  { week: "Module 22", title: "Team Building & Delegation", duration: "2 hours", skoolUrl: "https://www.skool.com/launch2revenue" },
  { week: "Module 23", title: "Analytics & Data-Driven Growth", duration: "2 hours", skoolUrl: "https://www.skool.com/launch2revenue" },
  { week: "Module 24", title: "Advanced Marketing Automation", duration: "3 hours", skoolUrl: "https://www.skool.com/launch2revenue" },
  { week: "Module 25", title: "Multiple Revenue Streams", duration: "2 hours", skoolUrl: "https://www.skool.com/launch2revenue" },
  { week: "Module 26", title: "Long-term Sustainability", duration: "2 hours", skoolUrl: "https://www.skool.com/launch2revenue" },
  { week: "Module 27", title: "Exit Strategy & Next Level", duration: "2 hours", skoolUrl: "https://www.skool.com/launch2revenue" }
];

const DashboardProgress = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState<Record<string, boolean>>({});

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate("/auth");
      return;
    }
    loadProgress(session.user.id);
  };

  const loadProgress = async (userId: string) => {
    const { data, error } = await supabase
      .from("course_progress")
      .select("*")
      .eq("user_id", userId);

    if (error) {
      toast.error("Error loading progress");
    } else {
      const progressMap: Record<string, boolean> = {};
      data?.forEach(item => {
        progressMap[item.module_week] = item.completed;
      });
      setProgress(progressMap);
    }
    setLoading(false);
  };

  const toggleModule = async (moduleWeek: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const newCompleted = !progress[moduleWeek];

    const { error } = await supabase
      .from("course_progress")
      .upsert({
        user_id: session.user.id,
        module_week: moduleWeek,
        completed: newCompleted,
        completed_at: newCompleted ? new Date().toISOString() : null
      }, {
        onConflict: "user_id,module_week"
      });

    if (error) {
      toast.error("Error updating progress");
    } else {
      setProgress({ ...progress, [moduleWeek]: newCompleted });
      toast.success(newCompleted ? "Module marked as complete" : "Module marked as incomplete");
    }
  };

  const completedCount = Object.values(progress).filter(Boolean).length;
  const totalModules = modules.length;
  const progressPercentage = Math.round((completedCount / totalModules) * 100);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto px-4 py-12 max-w-5xl">
        <Link to="/dashboard">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </Link>

        <div className="space-y-8">
          <div className="text-center space-y-4 animate-fade-in">
            <h1 className="text-4xl font-bold">Course Progress</h1>
            <p className="text-xl text-muted-foreground">
              Track your journey through all 27 modules
            </p>
          </div>

          {/* Progress Summary */}
          <Card className="border-2 animate-fade-in" style={{ animationDelay: "100ms" }}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-2xl font-bold">{progressPercentage}% Complete</h3>
                  <p className="text-muted-foreground">
                    {completedCount} of {totalModules} modules completed
                  </p>
                </div>
                <CheckCircle2 className="w-12 h-12 text-primary" />
              </div>
              <div className="w-full bg-muted rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-primary to-accent h-3 rounded-full transition-all duration-500"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Module List */}
          <div className="space-y-4">
            {modules.map((module, index) => (
              <Card
                key={module.week}
                className={`border-2 transition-all hover:shadow-lg hover:shadow-primary/20 animate-fade-in ${
                  progress[module.week] ? "border-primary bg-primary/5" : "border-border"
                }`}
                style={{ animationDelay: `${200 + index * 50}ms` }}
              >
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <Checkbox
                        checked={progress[module.week] || false}
                        onCheckedChange={() => toggleModule(module.week)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-primary mb-1">
                          {module.week}
                        </div>
                        <CardTitle className="text-xl">{module.title}</CardTitle>
                        <div className="mt-3">
                          <Button
                            variant="outline"
                            size="sm"
                            asChild
                          >
                            <a 
                              href={module.skoolUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-2"
                            >
                              Access on Skool
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground text-sm">
                      <Clock className="w-4 h-4" />
                      <span>{module.duration}</span>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardProgress;
